import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn, hasAdminAccess, isLoading } = useAdminAuth();
  const [loginState, setLoginState] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState(null);
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  useEffect(() => {
    if (hasAdminAccess) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [hasAdminAccess, navigate]);

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginState((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginSubmitting(true);
    setLoginError(null);

    try {
      const { error } = await signIn(loginState.email, loginState.password);
      if (error) {
        const msg = error.message ?? 'Unable to sign in';
        setLoginError(msg);
      }
    } catch (err) {
      console.error('Admin login failed', err);
      setLoginError(err instanceof Error ? err.message : 'Unable to sign in');
    } finally {
      setLoginSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-orange-200 bg-white p-8 shadow-warm-xl backdrop-blur">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4">
              <img src="/assets/images/logo.png" alt="Mrs Cater" className="mx-auto h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-semibold text-primary">
              Admin Sign In
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Platform operations console
            </p>
          </div>

          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Email</label>
              <div className="relative rounded-xl border border-orange-200 bg-orange-50/50">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-primary/60" size={18} />
                <input
                  name="email"
                  type="email"
                  value={loginState.email}
                  onChange={handleLoginChange}
                  required
                  autoComplete="email"
                  className="h-12 w-full rounded-xl bg-transparent pl-11 pr-4 text-sm text-gray-900 outline-none placeholder:text-gray-500 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder="admin@mrscater.com"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Password</label>
              <div className="relative rounded-xl border border-orange-200 bg-orange-50/50">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-primary/60" size={18} />
                <input
                  name="password"
                  type="password"
                  value={loginState.password}
                  onChange={handleLoginChange}
                  required
                  autoComplete="current-password"
                  className="h-12 w-full rounded-xl bg-transparent pl-11 pr-4 text-sm text-gray-900 outline-none placeholder:text-gray-500 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {loginError && (
              <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{loginError}</p>
            )}

            <button
              type="submit"
              disabled={loginSubmitting || isLoading}
              className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-warm-md hover:bg-[#A84F1F] disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
            >
              {loginSubmitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-600">
            All admin actions are logged and monitored
          </p>
        </div>
      </div>
    </div>
  );
}
